import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ReconciliationSummary = ({ summary, onComplete, onExport }) => {
  const summaryItems = [
    {
      label: 'Opening Balance',
      value: summary?.openingBalance,
      icon: 'TrendingUp',
      color: 'primary'
    },
    {
      label: 'Total Deposits',
      value: summary?.totalDeposits,
      icon: 'ArrowDownCircle',
      color: 'success'
    },
    {
      label: 'Total Withdrawals',
      value: summary?.totalWithdrawals,
      icon: 'ArrowUpCircle',
      color: 'error'
    },
    {
      label: 'Closing Balance',
      value: summary?.closingBalance,
      icon: 'DollarSign',
      color: 'secondary'
    }
  ];

  const isReconciled = summary?.matchedCount === summary?.totalCount && 
                       summary?.discrepancyCount === 0;

  return (
    <div className="bg-card rounded-lg shadow-sm border border-border">
      <div className="p-4 md:p-6 border-b border-border">
        <div className="flex items-center gap-3">
          <div className={`
            w-10 h-10 rounded-md flex items-center justify-center
            ${isReconciled ? 'bg-success/10' : 'bg-warning/10'}
          `}>
            <Icon 
              name={isReconciled ? 'CheckCircle2' : 'Clock'} 
              size={20} 
              color={isReconciled ? 'var(--color-success)' : 'var(--color-warning)'} 
            />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-heading font-semibold text-base md:text-lg">Reconciliation Summary</h3>
            <p className="caption text-muted-foreground">
              {summary?.accountName} • {summary?.period}
            </p>
          </div>
        </div>
      </div>
      <div className="p-4 md:p-6 space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {summaryItems?.map((item, index) => (
            <div key={index} className="bg-background rounded-lg p-4 border border-border">
              <div className="flex items-center gap-2 mb-2">
                <Icon name={item?.icon} size={16} color={`var(--color-${item?.color})`} />
                <span className="caption text-muted-foreground">{item?.label}</span>
              </div>
              <p className="data-text text-xl md:text-2xl font-semibold text-foreground">
                ${item?.value?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </p>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="bg-success/5 rounded-lg p-4 border border-success/20">
            <p className="caption text-muted-foreground mb-1">Matched</p>
            <p className="data-text text-2xl md:text-3xl font-semibold text-success">
              {summary?.matchedCount}
            </p>
          </div>
          <div className="bg-warning/5 rounded-lg p-4 border border-warning/20">
            <p className="caption text-muted-foreground mb-1">Unmatched</p>
            <p className="data-text text-2xl md:text-3xl font-semibold text-warning">
              {summary?.unmatchedCount}
            </p>
          </div>
          <div className="bg-error/5 rounded-lg p-4 border border-error/20">
            <p className="caption text-muted-foreground mb-1">Discrepancies</p>
            <p className="data-text text-2xl md:text-3xl font-semibold text-error">
              {summary?.discrepancyCount}
            </p>
          </div>
        </div>

        <div className={`
          rounded-lg p-4 border-2
          ${isReconciled 
            ? 'bg-success/5 border-success/20' :'bg-warning/5 border-warning/20'
          }
        `}>
          <div className="flex items-center gap-3">
            <Icon 
              name={isReconciled ? 'CheckCircle2' : 'AlertCircle'} 
              size={24} 
              color={isReconciled ? 'var(--color-success)' : 'var(--color-warning)'} 
            />
            <div className="flex-1 min-w-0">
              <p className="font-body font-semibold text-base md:text-lg">
                {isReconciled ? 'Reconciliation Complete' : 'Reconciliation In Progress'}
              </p>
              <p className="caption text-muted-foreground mt-1">
                {isReconciled 
                  ? 'All transactions matched successfully' 
                  : `${summary?.unmatchedCount + summary?.discrepancyCount} items need attention`
                }
              </p>
            </div>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-3">
          <Button
            variant="default"
            onClick={onComplete}
            disabled={!isReconciled}
            iconName="CheckCircle2"
            iconPosition="left"
            fullWidth
          >
            Complete Reconciliation
          </Button>
          <Button
            variant="outline"
            onClick={onExport}
            iconName="Download"
            iconPosition="left"
          >
            Export Report
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ReconciliationSummary;